/**
 * Compact binary genealogy preview for delete / shift-upline modals.
 */
import type { CSSProperties } from 'react';
import { Tag, Typography } from 'antd';

export type GenealogyNodeTag =
  | 'target'
  | 'deleted'
  | 'sub_deleted'
  | 'reparented'
  | 'moved'
  | 'new_upline'
  | 'placement'
  | 'unchanged';

export type GenealogyPreviewNode = {
  id: number;
  username: string;
  level_code?: string;
  position?: 'L' | 'R' | null;
  tag: GenealogyNodeTag;
  left?: GenealogyPreviewNode | null;
  right?: GenealogyPreviewNode | null;
};

export type GenealogyPreview = {
  path: Array<{ id: number; username: string; position?: 'L' | 'R' | null }>;
  focus: GenealogyPreviewNode;
  caption: string;
};

const TAG_META: Record<GenealogyNodeTag, { label: string; color: string }> = {
  target: { label: '目标', color: 'gold' },
  deleted: { label: '将删除', color: 'red' },
  sub_deleted: { label: '子账户删除', color: 'volcano' },
  reparented: { label: '改挂', color: 'purple' },
  moved: { label: '迁移', color: 'blue' },
  new_upline: { label: '新上级', color: 'green' },
  placement: { label: '安置节点', color: 'cyan' },
  unchanged: { label: '不变', color: 'default' },
};

const cardStyle: CSSProperties = {
  borderRadius: 10,
  padding: '12px 14px',
  background: 'linear-gradient(135deg, #1a2332 0%, #243044 100%)',
  border: '1px solid rgba(212, 175, 55, 0.22)',
  color: '#e8edf5',
};

function NodeCard({ node }: { node: GenealogyPreviewNode }) {
  const meta = TAG_META[node.tag] ?? TAG_META.unchanged;
  return (
    <div style={{ ...cardStyle, minWidth: 120 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
        <Tag color={meta.color} style={{ margin: 0 }}>
          {meta.label}
        </Tag>
        {node.position && (
          <Tag style={{ margin: 0 }}>{node.position === 'L' ? '左区' : '右区'}</Tag>
        )}
      </div>
      <div style={{ fontWeight: 600, fontSize: 13 }}>{node.username}</div>
      <div style={{ fontSize: 11, color: '#8a96a8', fontFamily: 'monospace' }}>
        #{node.id}
        {node.level_code ? ` · ${node.level_code}` : ''}
      </div>
    </div>
  );
}

function Slot({
  label,
  zone,
  node,
  zonePicker,
}: {
  label: string;
  zone: 'L' | 'R';
  node: GenealogyPreviewNode | null | undefined;
  zonePicker?: {
    selected: 'L' | 'R';
    onSelect: (zone: 'L' | 'R') => void;
    loading?: boolean;
  };
}) {
  const selectable = Boolean(zonePicker);
  const selected = zonePicker?.selected === zone;

  const emptySlot = (
    <div
      style={{
        ...cardStyle,
        minHeight: 72,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#6b7280',
        fontSize: 12,
        border: selected ? '2px solid #d4af37' : cardStyle.border,
        boxShadow: selected ? '0 0 0 1px rgba(212, 175, 55, 0.35)' : undefined,
      }}
    >
      {selectable ? (selected ? '已选 · 空位（含滑落）' : '点击选择此区') : '空位'}
    </div>
  );

  const content = node ? (
    <div
      style={{
        borderRadius: 10,
        border: selected ? '2px solid #d4af37' : '2px solid transparent',
        boxShadow: selected ? '0 0 0 1px rgba(212, 175, 55, 0.35)' : undefined,
      }}
    >
      <NodeCard node={node} />
    </div>
  ) : (
    emptySlot
  );

  return (
    <div style={{ flex: 1, minWidth: 140 }}>
      <div style={{ fontSize: 10, color: selected ? '#d4af37' : '#888', marginBottom: 6 }}>
        {label}
        {selectable && selected ? ' · 已选' : ''}
      </div>
      {selectable ? (
        <button
          type="button"
          disabled={zonePicker?.loading}
          onClick={() => zonePicker?.onSelect(zone)}
          style={{
            width: '100%',
            padding: 0,
            border: 'none',
            background: 'transparent',
            cursor: zonePicker?.loading ? 'wait' : 'pointer',
            textAlign: 'left',
            opacity: zonePicker?.loading && !selected ? 0.6 : 1,
          }}
        >
          {content}
        </button>
      ) : (
        content
      )}
    </div>
  );
}

export function GenealogyPreviewChart({
  data,
  zonePicker,
}: {
  data: GenealogyPreview;
  zonePicker?: {
    selected: 'L' | 'R';
    onSelect: (zone: 'L' | 'R') => void;
    loading?: boolean;
  };
}) {
  return (
    <div style={{ marginBottom: 12 }}>
      {data.path.length > 1 && (
        <div style={{ marginBottom: 10, fontSize: 12, lineHeight: 1.8 }}>
          <span style={{ color: '#888', marginRight: 6 }}>上级链</span>
          {data.path.map((crumb, index) => (
            <span key={crumb.id}>
              {index > 0 && <span style={{ color: '#999', margin: '0 4px' }}>›</span>}
              <span style={{ color: index === data.path.length - 1 ? '#d4af37' : '#555' }}>
                {crumb.username}
                {crumb.position ? ` (${crumb.position})` : ''}
              </span>
            </span>
          ))}
        </div>
      )}

      <NodeCard node={data.focus} />

      {(data.focus.left || data.focus.right || zonePicker) && (
        <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
          <Slot label="左区 (L)" zone="L" node={data.focus.left} zonePicker={zonePicker} />
          <Slot label="右区 (R)" zone="R" node={data.focus.right} zonePicker={zonePicker} />
        </div>
      )}

      <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 11 }}>
        {data.caption}
      </Typography.Text>
    </div>
  );
}

export function GenealogyBeforeAfter({
  before,
  after,
  afterZonePicker,
}: {
  before: GenealogyPreview;
  after: GenealogyPreview;
  afterZonePicker?: {
    selected: 'L' | 'R';
    onSelect: (zone: 'L' | 'R') => void;
    loading?: boolean;
  };
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <div>
        <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
          迁移前
        </Typography.Text>
        <GenealogyPreviewChart data={before} />
      </div>
      <div>
        <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
          迁移后
        </Typography.Text>
        {afterZonePicker && (
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 11 }}>
            点击左区 / 右区切换安置位置（含滑落），预览将自动刷新
          </Typography.Text>
        )}
        <GenealogyPreviewChart data={after} zonePicker={afterZonePicker} />
      </div>
    </div>
  );
}
